/** Static fractured-rock groups, owned and instanced by the existing LOD tiles. */
import * as THREE from 'three';
import { rngFromString, range, fallSheetPoint, type PlanetDef, type PlanetSampler, type TerrainSample, type RiverFall } from '@hyperlight/shared';
import { faceDir, FACES } from '../cubesphere';
import { ScatterSupport } from './scatterSupport';
import { floraHeight, floraTime } from './material';
import { makePooledFloraMesh } from './materialPool';
import { prepareScatterBounds } from './scatterLod';
import { hasLivingGround, groundDuneSuppression, groundCanyonAlbedo } from '../groundSurface';

export interface RockTile { face:number; level:number; u0:number; v0:number; size:number }
export interface RockPlacement { x:number; y:number; z:number; a:number; top:number; group:number; role:number; scale:number; seatDepth:number; fallId?:string; waterM?:number }
export interface RockScatterOptions { band:'near'|'forest'|'far'; pass:number; count:number; min:number; max:number; fadeFar?:number; fadeNear?:number; shoreline?:boolean }

type FallRockFrame={fall:RiverFall;waterM:number;base:THREE.Vector3;forward:THREE.Vector3;lateral:THREE.Vector3};
const fallRockFrames=new WeakMap<object,FallRockFrame[]>();
function rockFallFrames(sampler:PlanetSampler,R:number):FallRockFrame[]{
  const graph=sampler.rivers?.();if(!graph)return[];
  let frames=fallRockFrames.get(graph);if(frames)return frames;
  frames=graph.falls.map(fall=>{
    const impact=fallSheetPoint(fall,1,sampler.amplitude),up=new THREE.Vector3(impact.x,impact.y,impact.z);
    const forward=new THREE.Vector3(fall.end.x-fall.lip.x,fall.end.y-fall.lip.y,fall.end.z-fall.lip.z);
    forward.addScaledVector(up,-forward.dot(up)).normalize();
    return{fall,waterM:impact.ws*sampler.amplitude,base:up.multiplyScalar(R),forward,
      lateral:new THREE.Vector3().crossVectors(forward,up).normalize()};
  });
  fallRockFrames.set(graph,frames);return frames;
}

/** Fall direction belongs to exactly one tile, including cube-face edges. */
function ownsRockDirection(tile:RockTile,p:THREE.Vector3):boolean {
  let face=0,dot=-Infinity;
  for(let i=0;i<FACES.length;i++){
    const n=FACES[i].normal,q=p.x*n[0]+p.y*n[1]+p.z*n[2];if(q>dot){face=i;dot=q;}
  }
  if(face!==tile.face)return false;
  const b=FACES[face],u=(p.x*b.right[0]+p.y*b.right[1]+p.z*b.right[2])/dot,
    v=(p.x*b.up[0]+p.y*b.up[1]+p.z*b.up[2])/dot;
  return u>=tile.u0&&u<tile.u0+tile.size&&v>=tile.v0&&v<tile.v0+tile.size;
}

/** A common planet-local fracture axis, projected onto the curved surface.
 * Neighboring groups agree even across cube-face and LOD tile boundaries. */
export function rockStrike(up:THREE.Vector3, axis:THREE.Vector3, out:THREE.Vector3):THREE.Vector3 {
  out.copy(axis).addScaledVector(up,-axis.dot(up));
  if(out.lengthSq()<.0001)out.crossVectors(up,Math.abs(up.y)<.9?new THREE.Vector3(0,1,0):new THREE.Vector3(1,0,0));
  return out.normalize();
}

export function buildRockScatter(def:PlanetDef, sampler:PlanetSampler, geo:THREE.BufferGeometry, tile:RockTile,
  opts:RockScatterOptions):{mesh:THREE.InstancedMesh; rocks:RockPlacement[]}|null {
  if(opts.count<=0)return null;
  const rng=rngFromString(`${def.seed}:rock-groups:${tile.face}:${tile.level}:${tile.u0}:${tile.v0}:${opts.pass}`);
  const strata=rngFromString(`${def.seed}:strata`), axis=new THREE.Vector3(strata()-.5,strata()-.5,strata()-.5).normalize();
  const slots=opts.band==='near'?6:3, matrices:THREE.Matrix4[]=[], rocks:RockPlacement[]=[];
  const ts:TerrainSample={h:0,temp:0,moist:0,river:0,mountain:0,flowx:0,flowy:0,flowz:0,town:0,road:8};
  const support=new ScatterSupport(sampler,def), up=new THREE.Vector3(), pos=new THREE.Vector3();
  const strike=new THREE.Vector3(), local=new THREE.Vector3(), quat=new THREE.Quaternion(), yaw=new THREE.Quaternion();
  const Y=new THREE.Vector3(0,1,0), scale=new THREE.Vector3(), matrix=new THREE.Matrix4(), dir:number[]=[0,0,0];
  geo.computeBoundingBox();
  const bb=geo.boundingBox!, footprintRadius=geo.userData.rockFootprintRadius as number;
  // The existing rock geometry carries planet-specific vertex pigment. Divide
  // out only its mean for canyon instances, retaining each fracture's tone.
  const base=[0,0,0],colors=geo.getAttribute('color');
  for(let i=0;i<colors.count;i++){base[0]+=colors.getX(i);base[1]+=colors.getY(i);base[2]+=colors.getZ(i);}
  for(let i=0;i<3;i++)base[i]=Math.max(.015,base[i]/colors.count);
  const canyonTints:number[]=[];
  const emit=(point:THREE.Vector3,s:number,bearingJitter:number,group:number,role:number,random=rng,riverEdge=false):boolean=>{
    up.copy(point).normalize();sampler.sample(up.x,up.y,up.z,ts,false);
    const duneSuppression=groundDuneSuppression(ts.duneSand??0,'rock');
    const canyonCoverage=Math.max(ts.canyonGround??0,ts.canyonRock??0),heightM=ts.h*sampler.amplitude;
    if((ts.glacierIceM??0)>1)return false;
    if((ts.lavaDepthM??0)>0)return false;
    const r=def.radius+ts.h*sampler.amplitude, footprint=footprintRadius*s;
    if(riverEdge){
      const hit=sampler.rivers().carveAt(up.x,up.y,up.z);
      if(!hit?.fall||hit.s<hit.fall.end.s+2||hit.dist-footprint<1.2||(ts.riverWs??-1e9)<-1e8)return false;
    }
    if(opts.shoreline&&(def.seaRadius===null||r>def.seaRadius+.55||r<def.seaRadius-1.25))return false;
    const depth=((ts.riverWs??-1e9)-ts.h)*sampler.amplitude;
    if((riverEdge?depth>1.0:ts.river>.3||depth>-.4)||ts.town>.002
      ||(ts.road<8&&ts.road<footprint+1.2)||(!opts.shoreline&&def.seaRadius!==null&&r<def.seaRadius+.5))return false;
    const sy=s*range(random,riverEdge?1.45:.88,riverEdge?1.65:1.1);
    // A substantial buried skirt tolerates moderate slopes. The support solve
    // rejects disconnected ledges and samples every fragment independently.
    const seat=support.radius(up,r,footprint*.88,Math.max(.18,(bb.max.y-bb.min.y)*sy*(riverEdge?.85:.46)),true,1.2,opts.shoreline?-2.4:.5,riverEdge?2.4:undefined);
    if(seat===null)return false;
    if(riverEdge&&seat-def.radius+(bb.max.y-.045)*sy<(ts.riverWs??-1e9)*sampler.amplitude+.35)return false;
    if(seat-r+(bb.max.y-.045)*sy<.035)return false; // discard completely buried chips
    if(opts.shoreline&&seat+(bb.max.y-.045)*sy<def.seaRadius!+.12)return false; // exposed stones only
    pos.copy(up).multiplyScalar(seat-.045*sy);
    rockStrike(up,axis,strike);
    quat.setFromUnitVectors(Y,up);local.copy(strike).applyQuaternion(quat.clone().invert());
    quat.multiply(yaw.setFromAxisAngle(Y,-Math.atan2(local.z,local.x)+bearingJitter));
    scale.set(s,sy,s);matrix.compose(pos,quat,scale);
    const tint=range(random,.94,1.04);
    // A filtered primary still supplies the original group frame and child
    // draws. Removing sand-covered fragments must not reseed neighbouring
    // outcrops, or refill the original group budget with replacement stones.
    if(duneSuppression>0&&rngFromString(`${def.seed}:dune-rock:${up.x}:${up.y}:${up.z}`)()<duneSuppression)return true;
    matrices.push(matrix.clone());
    rocks.push({x:pos.x,y:pos.y,z:pos.z,a:footprint,top:bb.max.y*sy,group,role,scale:s,seatDepth:r-pos.length(),...(riverEdge?{waterM:ts.riverWs!*sampler.amplitude}:{})});
    if(canyonCoverage>0){
      const mineral=groundCanyonAlbedo(heightM,{r:0,g:0,b:0}),shade=rngFromString(`${def.seed}:canyon-stone:${group}`)(),dark=.15+.45*shade;
      [mineral.r,mineral.g,mineral.b].forEach((v,i)=>{const pigment=v*(1-dark)+[.145,.095,.075][i]*dark;canyonTints.push(tint*(1+(pigment/base[i]-1)*canyonCoverage));});
    }else canyonTints.push(tint,tint,tint);
    return true;
  };
  let groups=0;
  for(let attempt=0;attempt<opts.count*3&&groups<opts.count;attempt++) {
    faceDir(tile.face,tile.u0+rng()*tile.size,tile.v0+rng()*tile.size,dir);
    const center=new THREE.Vector3(...dir), s=range(rng,opts.min,opts.max), jitter=range(rng,-.45,.45);
    if(!emit(center,s,jitter,groups,0))continue;
    const root=pos.clone(), mainUp=up.clone(), along=strike.clone(), across=new THREE.Vector3().crossVectors(mainUp,along).normalize();
    const radius=footprintRadius*s, side=rng()<.5?-1:1;
    for(let role=1;role<slots;role++) {
      const medium=role<3, childScale=s*range(rng,medium?.32:.09,medium?.52:.22);
      // Related broken pieces occupy a short irregular seam, not a ring.
      const distance=radius*range(rng,medium?.72:1.05,medium?1.05:1.7);
      const point=root.clone().addScaledVector(along,distance*(role===2?-side:side))
        .addScaledVector(across,radius*range(rng,-.4,.45));
      emit(point,childScale,jitter+range(rng,medium?-.6:-1.1,medium?.6:1.1),groups,role);
    }
    groups++;
  }
  // Dry icy flanks redistribute existing groups. Living falls get at most 12
  // small bank groups (24 stones) in this existing instanced batch, with a
  // bounded support-attempt budget. Ordinary seeds and matrices stay intact.
  if(opts.band==='forest'&&opts.pass===0&&!opts.shoreline&&(opts.count>=3||(hasLivingGround(def)&&!!def.atmosphere))&&rocks.length){
    const living=hasLivingGround(def)&&!!def.atmosphere;
    const frames=rockFallFrames(sampler,def.radius),quota=living?12:Math.min(2,Math.max(1,Math.floor(opts.count/8)));
    const replaceable=[...new Set(rocks.filter(r=>r.role===0).map(r=>r.group))].reverse();
    faceDir(tile.face,tile.u0+tile.size*.5,tile.v0+tile.size*.5,dir);
    const reach=tile.size*def.radius*1.5+70;
    let replaced=0,attemptsLeft=living?192:32;
    for(const frame of frames){
      if(replaced>=quota||(!living&&!replaceable.length)||attemptsLeft<=0)break;
      const{fall,base,forward,lateral,waterM}=frame;
      // Long cascade receivers put their shallow outflow well beyond impact.
      // Keep the old seed/positions for ordinary falls; move these candidates
      // to the actual receiving reach before applying the same support checks.
      const outflow=fall.escarpmentId?Math.max(0,fall.end.s-fall.lip.s-fall.runM-4):0;
      if(Math.hypot(base.x-dir[0]*def.radius,base.y-dir[1]*def.radius,base.z-dir[2]*def.radius)>reach)continue;
      // Every bank has a fixed owner before support attempts, preventing a
      // nearby tile from accepting another attempt for the same bank.
      for(const bank of[-1,1]){
        if(replaced>=quota||(!living&&!replaceable.length)||attemptsLeft<=0)break;
        const riverEdge=hasLivingGround(def)&&!!def.atmosphere&&!fall.nativeCliff;
        const preferred=base.clone().addScaledVector(lateral,bank*(riverEdge?fall.half+3:fall.half*2.6+6.5)).addScaledVector(forward,outflow+(riverEdge?12:6));
        if(!ownsRockDirection(tile,preferred))continue;
        const targetRng=rngFromString(`${def.seed}:fall-talus:${fall.id}:${bank}`),group=riverEdge?5000+replaced:replaceable[0];
        if(group===undefined)continue;
        const budget=riverEdge?2:rocks.filter(r=>r.group===group).length;
        const preferredScale=range(targetRng,Math.max(opts.min,opts.max*.60),Math.max(opts.min,opts.max*.84)),jitter=range(targetRng,-.45,.45);
        for(let attempt=0;attempt<(riverEdge?24:16)&&attemptsLeft>0;attempt++){
          attemptsLeft--;
          const wet=riverEdge&&attempt<16;
          const s=wet?2.0+(attempt%3)*.35:attempt<8?preferredScale:Math.max(opts.min,preferredScale*.8);
          const radius=footprintRadius*s;
          const point=base.clone().addScaledVector(lateral,bank*(wet?fall.half+radius*.30+(attempt%4)*.5:fall.half*2.6+radius+.9+(attempt%4)*3))
            .addScaledVector(forward,outflow+(wet?7+Math.floor(attempt/4)*4:3+radius*.6+Math.floor((attempt%8)/4)*4)).normalize();
          if(!ownsRockDirection(tile,point))continue;
          sampler.sample(point.x,point.y,point.z,ts,false);
          const above=(ts.h-(wet?(ts.riverWs??-1e9):waterM/sampler.amplitude))*sampler.amplitude;
          if(wet){const hit=sampler.rivers().carveAt(point.x,point.y,point.z);if(!hit||hit.dist-radius<1.2||above<-.6||above>1.5)continue;}
          else if(above<.4||above>Math.min(8,2+fall.dropM*.35))continue;
          const start=rocks.length;
          if(!emit(point,s,jitter,group,0,targetRng,wet)||rocks.length===start)continue;
          const root=pos.clone(),along=strike.clone(),across=new THREE.Vector3().crossVectors(up,along).normalize();
          // Keep fractured companions when the replaced group had room. Full
          // support checks seat each stone separately, with the same colliders.
          for(let role=1;role<Math.min(slots,budget);role++){
            const childScale=s*range(targetRng,.32,.52),distance=radius*range(targetRng,.72,1.05);
            const child=root.clone().addScaledVector(along,distance*(role===2?-1:1)).addScaledVector(across,radius*range(targetRng,-.4,.45));
            if(ownsRockDirection(tile,child))emit(child,childScale,jitter+range(targetRng,-.6,.6),group,role,targetRng,wet);
          }
          for(let i=start;i<rocks.length;i++)rocks[i].fallId=fall.id;
          for(let i=start-1;i>=0;i--)if(rocks[i].group===group){rocks.splice(i,1);matrices.splice(i,1);canyonTints.splice(i*3,3);}
          if(!riverEdge)replaceable.shift();replaced++;break;
        }
      }
    }
  }
  if(!rocks.length)return null;
  const mesh=makePooledFloraMesh(geo,rocks.length,{birth:floraTime(),height:floraHeight(geo),rigid:true,fadeFar:opts.fadeFar??0,fadeNear:opts.fadeNear??0});
  matrices.forEach((m,i)=>{mesh.setMatrixAt(i,m);mesh.setColorAt(i,new THREE.Color().setRGB(canyonTints[i*3],canyonTints[i*3+1],canyonTints[i*3+2]));});
  mesh.instanceMatrix.needsUpdate=true;mesh.instanceColor!.needsUpdate=true;
  mesh.userData={rigid:true,rockGroups:true,shoreline:!!opts.shoreline,rockBand:opts.band,rockEvidence:rocks,fadeFar:opts.fadeFar??0,fadeNear:opts.fadeNear??0};
  mesh.castShadow=opts.band!=='far';mesh.receiveShadow=true;mesh.visible=false;prepareScatterBounds(mesh);
  return {mesh,rocks};
}
